/** @public */
export enum SubscriberStatus {
    SUBSCRIBED = 'subscribed',
    DISMISSED = 'dismissed',
    DENIED = 'denied',
    NOT_DETERMINED = 'not_determined',
}

/**
 * Map of stored numerical values for {@link SubscriberStatus}.
 *
 * @internal
 */
export const SubscriberStatusStorageValue: Record<SubscriberStatus, number> = {
    [SubscriberStatus.NOT_DETERMINED]: 0,
    [SubscriberStatus.DISMISSED]: 1,
    [SubscriberStatus.SUBSCRIBED]: 2,
    [SubscriberStatus.DENIED]: -1,
} as const

/**
 * Parses string or numerical values into corresponding {@link SubscriberStatus}.
 *
 * @internal
 */
export const parseSubscriberStatus = (status: string | number): SubscriberStatus => {
    if (typeof status === 'string') {
        status = status.toLowerCase()
    }

    switch (status) {
        case SubscriberStatus.SUBSCRIBED:
        case SubscriberStatusStorageValue[SubscriberStatus.SUBSCRIBED]:
            return SubscriberStatus.SUBSCRIBED
        case SubscriberStatus.DISMISSED:
        case SubscriberStatusStorageValue[SubscriberStatus.DISMISSED]:
            return SubscriberStatus.DISMISSED
        case 'blocked': // legacy value
        case SubscriberStatus.DENIED:
        case SubscriberStatusStorageValue[SubscriberStatus.DENIED]:
            return SubscriberStatus.DENIED
        case 'none': // legacy value
        case SubscriberStatus.NOT_DETERMINED:
        case SubscriberStatusStorageValue[SubscriberStatus.NOT_DETERMINED]:
        default:
            return SubscriberStatus.NOT_DETERMINED
    }
}

/**
 * Returns a user-friendly variant of the {@link SubscriberStatus} for logging
 * or user displayed strings.
 *
 * @example
 * ```typescript
 * logger.info(`Subscriber status: ${subscriberStatusToSentenceString(SubscriberStatus.NOT_DETERMINED)}`)
 *
 * // Logs: "Subscriber status: not determined"
 * ```
 *
 * @internal
 */
export const subscriberStatusToSentenceString = (status: SubscriberStatus): string => {
    switch (status) {
        case SubscriberStatus.NOT_DETERMINED:
            return 'not determined'
        default:
            return status
    }
}
